"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MinnesotaRules = void 0;
exports.effectiveMinnesotaStatus = effectiveMinnesotaStatus;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const unwrap_1 = require("../../unwrap");
const FEDERAL_1 = require("./FEDERAL");
/**
 * Minnesota (MPCA) accumulation limits, Minn. R. 7045.0292, verified against
 * MPCA fact sheet w-hw1-05 (Sept 2021).
 *
 * Minnesota's clocks are the federal numbers with one deep structural
 * difference: a Minnesota VSQG has NO standing central clock at all. Its
 * constraint is a 1,000 kg on-site quantity cap (subp. 6), and exceeding it
 * escalates the facility to SQG treatment (subp. 6 item H) — a facility-level
 * quantity trigger the per-container clock cannot express, owned by the
 * on-site cap rollup rather than this class.
 *
 * Minnesota sets its clocks and caps by the MORE STRINGENT of the
 * federal generator status and Minnesota's own determination, named as the
 * `effectiveGeneratorStatus` derivation below. Minnesota's own tier vocabulary
 * additionally includes the MiniQG (an MPCA licensing subset of VSQG), which
 * this class must interpret — see the derivation.
 */
const MN_STATE_STATUS_DOMAIN = [
    "LARGE_QUANTITY_GENERATOR",
    "SMALL_QUANTITY_GENERATOR",
    "VERY_SMALL_QUANTITY_GENERATOR",
    "MINI_QUANTITY_GENERATOR",
];
/**
 * The stricter-of rule as data: whichever axis reports the more stringent
 * tier sets it. Total and non-overlapping over the twenty status pairs, both
 * enforced. `as const` so the claim rows can structurally exclude the axes it
 * reads (see `DerivedAxes`); `satisfies` ties the table to the determinations
 * type, so every determination a claim can key on has a declared derivation.
 */
const MN_DERIVATIONS = {
    effectiveGeneratorStatus: {
        from: ["generatorStatus", "stateGeneratorStatus"],
        label: "Effective Minnesota tier",
        citation: "Minn. R. 7045.0206",
        description: (0, unwrap_1.unwrap) `A facility's Minnesota generator tier is the stricter of federal
      generator status and MN generator status.
      LQG on either -> LQG. That is the strictest possible so it doesn't matter if the
      other is unknown. Otherwise if either is unknown, we can't be sure it's not
      LQG, so just honestly report unknown.
      MiniQG is an MPCA licensing subset of VSQG and follows the VSQG accumulation 
      standards (Metro County stricter limits are not modeled).`,
        cases: [
            // LQG on either axis wins outright, even if the other is unknown.
            { when: { generatorStatus: "LQG" }, produces: "LQG" },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            // Both axes known, neither LQG: the stricter tier wins; MiniQG counts
            // as VSQG (see the description).
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "MINI_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "VSQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "MINI_QUANTITY_GENERATOR" },
                produces: "VSQG",
            },
            // Insufficient information
            { when: { generatorStatus: "SQG", stateGeneratorStatus: null }, produces: null },
            { when: { generatorStatus: "VSQG", stateGeneratorStatus: null }, produces: null },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "MINI_QUANTITY_GENERATOR" },
                produces: null,
            },
            { when: { generatorStatus: null, stateGeneratorStatus: null }, produces: null },
        ],
    },
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste"],
        label: "Minnesota Effective Subclassification",
        citation: "Minn. R. 7045.1400",
        description: (0, unwrap_1.unwrap) `Whether waste is Universal Waste depends on whether the state has adopted the
    relevant category (the UNIVERSAL_WASTE information coming in is only whether the line is UW
    federally). The determinations as to whether MN has adopted a particular category are made
    elsewhere and should simply be taken as given here.
    Additionally, MN has not adopted 40 CFR 266 Subpart P, so pharma waste is fully regulated
    haz waste on 7045.0292's ordinary standards (the facility's Subpart P flag is ignored).`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "ORDINARY",
            },
            {
                when: { wasteKind: "USED_OIL" },
                produces: "USED_OIL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL" },
                produces: "ORDINARY",
            },
            {
                when: { wasteKind: null },
                produces: "ORDINARY",
            },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
const MN_VSQG_CENTRAL_NOTE = (0, unwrap_1.unwrap) `
  MN VSQG containers have no time limit individually. Subp. 6 instead caps facility-wide accumulation at
  1,000 kg, and exceeding that immediately converts you to an SQG with 180 days from the exceedance date
  to ship or treat (https://www.revisor.mn.gov/rules/7045.0292/#rule.7045.0292.6.H).
  Those facility-wide quantity caps are tracked outside this table — the on-site cap rollup enforces
  Minnesota's numbers (see getOnSiteCap in ../onSiteCaps) — while the escalation consequence of a
  breach is surfaced to the facility rather than applied automatically.`;
const MN_SATELLITE_NOTE = (0, unwrap_1.unwrap) `
  Minn. R. 7045.0292, subp. 8: 55 gallons of hazardous waste or one quart of acute hazardous
  waste triggers 3-day move window (same as federal).
  Three assumptions to confirm:
  (1) Subp. 8 caps acute haz waste by volume only (one quart); our code also
  imposes a 1 kg cap on acute haz waste from federal 262.15(a).
  Does the federal cap apply to MN? It does if "RCRA is a floor". As it stands today, 
  we DO enforce the 1kg cap alongside the 1qt cap, so we can only be wrong in the too-strict direction.
  (2) Volume caps reach every generator status, including VSQGs and MiniQGs. SAA containers
  are not counted towards VSQG's 1,000 kg total on-site accumulation count.
  (3) subp. 8's limits apply per WASTE STREAM, while we judge them per accumulation
  area — over-strict for an area holding multiple streams, never under-strict.`;
const MN_UNIVERSAL_WASTE_ADOPTED_NOTE = (0, unwrap_1.unwrap) `
  Minn. R. 7045.1400 adopts the federal universal waste standards (40 CFR 273) by reference,
  so federal citations are shown.`;
const MN_USED_OIL_NOTE = (0, unwrap_1.unwrap) `
  MN regulates used oil under its own parts (Minn. R. 7045.0790 to 7045.0990), mirroring
  40 CFR part 279: a recycled-materials program with no clocks or caps. Recycled used oil
  is subject only to those parts (7045.0125, subp. 3a), and the generator storage standards
  (7045.0855, subp. 2) set no accumulation time limit.`;
const MN_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION — Minn. R. 7045.0292 ─────────────────────────
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: "LQG" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: {
                kind: "TIME",
                days: 90,
                citation: "Minn. R. 7045.0292, subp. 1",
                jurisdiction: "MN",
            },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: false },
        expect: {
            clock: {
                kind: "TIME",
                days: 180,
                citation: "Minn. R. 7045.0292, subp. 5",
                jurisdiction: "MN",
            },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: true },
        expect: {
            clock: {
                kind: "TIME",
                days: 270,
                citation: "Minn. R. 7045.0292, subp. 9",
                jurisdiction: "MN",
            },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: "VSQG" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "Minn. R. 7045.0292, subp. 6", jurisdiction: "MN" },
        },
        note: MN_VSQG_CENTRAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: null },
        when: { areaType: "CENTRAL" },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — Minn. R. 7045.0292, subp. 8 ──────────────
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: {
                kind: "VOLUME",
                nonAcuteGal: 55,
                acuteGal: 0.25,
                acuteKg: 1,
                moveWindowDays: 3,
                citation: "Minn. R. 7045.0292, subp. 8",
                jurisdiction: "MN",
            },
            clock: { kind: "NO_LIMIT", citation: "Minn. R. 7045.0292, subp. 8", jurisdiction: "MN" },
        },
        note: MN_SATELLITE_NOTE,
    },
    // ── UNIVERSAL WASTE — adopted by reference, Minn. R. 7045.1400 ─────────
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: MN_UNIVERSAL_WASTE_ADOPTED_NOTE,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: MN_UNIVERSAL_WASTE_ADOPTED_NOTE,
    },
    // ── USED OIL — Minnesota's own program mirrors part 279 ────────────────
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "Minn. R. 7045.0855, subp. 2", jurisdiction: "MN" },
        },
        note: MN_USED_OIL_NOTE,
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "Minn. R. 7045.0125, subp. 3a", jurisdiction: "MN" },
            clock: { kind: "NO_LIMIT", citation: "Minn. R. 7045.0855, subp. 2", jurisdiction: "MN" },
        },
        note: MN_USED_OIL_NOTE,
    },
];
/**
 * The imperative twin of the `effectiveGeneratorStatus` derivation above.
 * Sharing `MnDeterminations` keeps the two from drifting on shape; the sweep
 * holds their contents to each other.
 */
function effectiveMinnesotaStatus(params) {
    const { generatorStatus } = params;
    const stateGeneratorStatus = MN_STATE_STATUS_DOMAIN.find((status) => status === params.stateGeneratorStatus) ?? null;
    if (generatorStatus === "LQG" || stateGeneratorStatus === "LARGE_QUANTITY_GENERATOR") {
        return "LQG";
    }
    if (generatorStatus === null || stateGeneratorStatus === null) {
        return null;
    }
    if (generatorStatus === "SQG" || stateGeneratorStatus === "SMALL_QUANTITY_GENERATOR") {
        return "SQG";
    }
    // Only VSQG-tier combinations remain: the federal axis can only be VSQG
    // here, and the state axis only VSQG or its MiniQG subset.
    if (generatorStatus === "VSQG" ||
        stateGeneratorStatus === "VERY_SMALL_QUANTITY_GENERATOR" ||
        stateGeneratorStatus === "MINI_QUANTITY_GENERATOR") {
        return "VSQG";
    }
    // Exhaustiveness guard: only accepts `never`, so an unhandled status
    // combination fails to compile.
    return ((..._) => null)(generatorStatus, stateGeneratorStatus);
}
function effectiveMnWasteKind(ctx) {
    if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
        return "UNIVERSAL_WASTE";
    }
    if (ctx.wasteKind === "USED_OIL") {
        return "USED_OIL";
    }
    return "ORDINARY";
}
/** Minnesota (MPCA) accumulation limits, Minn. R. 7045.0292. */
class MinnesotaRules extends FEDERAL_1.StateRules {
    constructor() {
        super(...arguments);
        this.expectations = MN_EXPECTATIONS;
        this.derivations = MN_DERIVATIONS;
        this.stateStatusDomain = MN_STATE_STATUS_DOMAIN;
    }
    centralClock(ctx) {
        const effectiveKind = effectiveMnWasteKind(ctx);
        if (effectiveKind === "UNIVERSAL_WASTE") {
            return super.centralClock(ctx);
        }
        if (effectiveKind === "USED_OIL") {
            // Minnesota's own used-oil generator storage standards set no
            // accumulation time limit, mirroring part 279.
            return { kind: "NO_LIMIT", citation: "Minn. R. 7045.0855, subp. 2", jurisdiction: "MN" };
        }
        const effective = effectiveMinnesotaStatus(ctx);
        if (effective === "LQG") {
            return {
                kind: "TIME",
                days: 90,
                citation: "Minn. R. 7045.0292, subp. 1",
                jurisdiction: "MN",
            };
        }
        if (effective === "SQG") {
            if (ctx.mustTransport200Miles) {
                return {
                    kind: "TIME",
                    days: 270,
                    citation: "Minn. R. 7045.0292, subp. 9",
                    jurisdiction: "MN",
                };
            }
            return {
                kind: "TIME",
                days: 180,
                citation: "Minn. R. 7045.0292, subp. 5",
                jurisdiction: "MN",
            };
        }
        if (effective === "VSQG") {
            // No time limit — subp. 6 caps a VSQG by quantity (1,000 kg), and
            // exceeding it escalates the facility to SQG treatment. Facility-wide
            // triggers the on-site cap rollup owns, not a per-container clock.
            return { kind: "NO_LIMIT", citation: "Minn. R. 7045.0292, subp. 6", jurisdiction: "MN" };
        }
        return { kind: "UNDETERMINED" };
    }
    satelliteCaps(ctx) {
        const effectiveKind = effectiveMnWasteKind(ctx);
        if (effectiveKind === "UNIVERSAL_WASTE") {
            return super.satelliteCaps(ctx);
        }
        if (effectiveKind === "USED_OIL") {
            // Recycled used oil is subject only to the used-oil parts, which
            // impose no satellite caps.
            return { kind: "NO_LIMIT", citation: "Minn. R. 7045.0125, subp. 3a", jurisdiction: "MN" };
        }
        // Federal numbers under Minnesota's own regulation, reaching every tier:
        // satellite status is what exempts waste from the VSQG's 1,000 kg
        // accumulation count, so the caps bind VSQGs and MiniQGs too. The acute
        // mass figure is the federal stand-in for Minnesota's quart-only limit —
        // over-strict, never under-strict (see the satellite expectation note).
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "Minn. R. 7045.0292, subp. 8",
            jurisdiction: "MN",
        };
    }
    satelliteClock(ctx) {
        const effectiveKind = effectiveMnWasteKind(ctx);
        if (effectiveKind === "UNIVERSAL_WASTE") {
            return super.satelliteClock(ctx);
        }
        if (effectiveKind === "USED_OIL") {
            return { kind: "NO_LIMIT", citation: "Minn. R. 7045.0855, subp. 2", jurisdiction: "MN" };
        }
        // Subp. 8 sets no standing clock on satellite accumulation — the only
        // time limit is the breach-triggered 3-day transfer window, carried on
        // the caps.
        return { kind: "NO_LIMIT", citation: "Minn. R. 7045.0292, subp. 8", jurisdiction: "MN" };
    }
}
exports.MinnesotaRules = MinnesotaRules;
//# sourceMappingURL=MN.js.map